import { useState, useEffect, useCallback, useRef } from 'react';
import type { ProfileStats, ProfileHistoryEntry } from '@codedrill/shared';
import { useAuthStore } from '../store/useAuthStore';
import {
  apiGetProfileStats,
  apiGetProfileHistory,
  apiUpdateProfile,
} from '../api/profile';

interface ProfileData {
  stats: ProfileStats | null;
  history: ProfileHistoryEntry[];
  total: number;
  page: number;
  loading: boolean;
  error: string | null;
}

interface UseProfileResult extends ProfileData {
  setPage: (page: number) => void;
  updateAvatar: (file: File) => Promise<void>;
  updateUsername: (username: string) => Promise<void>;
  uploading: boolean;
}

const PAGE_SIZE = 20;

/**
 * Owns all profile data fetching and mutations.
 * Stats + first history page are fetched once and cached in module scope
 * so navigating away and back doesn't re-fetch unnecessarily.
 */
let statsCache: ProfileStats | null = null;

export function useProfile(): UseProfileResult {
  const updateUser = useAuthStore((s) => s.updateUser);

  const [data, setData] = useState<ProfileData>({
    stats: statsCache,
    history: [],
    total: 0,
    page: 1,
    loading: statsCache === null,
    error: null,
  });
  const [uploading, setUploading] = useState(false);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    setData((d) => ({ ...d, loading: true, error: null }));

    Promise.all([
      statsCache ? Promise.resolve({ stats: statsCache }) : apiGetProfileStats(),
      apiGetProfileHistory(data.page, PAGE_SIZE),
    ])
      .then(([statsRes, histRes]) => {
        if (cancelRef.current) return;
        statsCache = statsRes.stats;
        setData((d) => ({
          ...d,
          stats: statsRes.stats,
          history: histRes.entries,
          total: histRes.total,
          loading: false,
        }));
      })
      .catch((err: unknown) => {
        if (cancelRef.current) return;
        setData((d) => ({
          ...d,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to load profile',
        }));
      });

    return () => { cancelRef.current = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.page]);

  const setPage = useCallback((page: number) => {
    setData((d) => ({ ...d, page }));
  }, []);

  /**
   * Client-side avatar upload flow:
   * 1. Read file as data URL for instant preview (non-blocking via FileReader)
   * 2. Persist URL via PATCH /profile/me
   * 3. Update global auth store so header avatar updates immediately
   *
   * In production replace step 2 with a direct-to-CDN upload
   * and send back the CDN URL.
   */
  const updateAvatar = useCallback(async (file: File) => {
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const res = await apiUpdateProfile({ avatarUrl: dataUrl });
      updateUser(res.user);
    } finally {
      setUploading(false);
    }
  }, [updateUser]);

  const updateUsername = useCallback(async (username: string) => {
    const res = await apiUpdateProfile({ username });
    updateUser(res.user);
  }, [updateUser]);

  return { ...data, setPage, updateAvatar, updateUsername, uploading };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
